/**
 * QR Code Block - Editor Component
 *
 * @package BrandyBlocks
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, PanelColorSettings } from '@wordpress/block-editor';
import {
	PanelBody,
	Placeholder,
	RadioControl,
	RangeControl,
	TextControl,
	ToggleControl,
} from '@wordpress/components';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Edit component for QR Code block.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes
 * @param {Function} props.setAttributes
 */
export default function Edit( { attributes, setAttributes } ) {
	const { content, contentType, size, fgColor, bgColor, quietZone, caption, showDownload } =
		attributes;

	const blockProps = useBlockProps( { className: 'brandy-qr' } );

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'QR Code', 'brandy-blocks' ) } initialOpen={ true }>
					<RadioControl
						label={ __( 'Content type', 'brandy-blocks' ) }
						selected={ contentType }
						options={ [
							{ label: __( 'URL', 'brandy-blocks' ), value: 'url' },
							{ label: __( 'Text', 'brandy-blocks' ), value: 'text' },
						] }
						onChange={ ( val ) => setAttributes( { contentType: val } ) }
					/>
					<TextControl
						label={ __( 'Content', 'brandy-blocks' ) }
						value={ content }
						type={ contentType === 'url' ? 'url' : 'text' }
						placeholder={
							contentType === 'url'
								? __( 'https://example.com', 'brandy-blocks' )
								: __( 'Enter text…', 'brandy-blocks' )
						}
						onChange={ ( val ) => setAttributes( { content: val } ) }
					/>
					<RangeControl
						label={ __( 'Size (px)', 'brandy-blocks' ) }
						value={ size }
						onChange={ ( val ) => setAttributes( { size: val } ) }
						min={ 80 }
						max={ 600 }
						step={ 10 }
					/>
					<RangeControl
						label={ __( 'Quiet zone', 'brandy-blocks' ) }
						help={ __( 'White border cells around the QR code.', 'brandy-blocks' ) }
						value={ quietZone }
						onChange={ ( val ) => setAttributes( { quietZone: val } ) }
						min={ 0 }
						max={ 4 }
						step={ 1 }
					/>
				</PanelBody>

				<PanelColorSettings
					title={ __( 'Colors', 'brandy-blocks' ) }
					initialOpen={ false }
					colorSettings={ [
						{
							label: __( 'Foreground', 'brandy-blocks' ),
							value: fgColor,
							onChange: ( val ) => setAttributes( { fgColor: val || '#000000' } ),
						},
						{
							label: __( 'Background', 'brandy-blocks' ),
							value: bgColor,
							onChange: ( val ) => setAttributes( { bgColor: val || '#ffffff' } ),
						},
					] }
				/>

				<PanelBody title={ __( 'Display', 'brandy-blocks' ) } initialOpen={ false }>
					<TextControl
						label={ __( 'Caption', 'brandy-blocks' ) }
						value={ caption }
						onChange={ ( val ) => setAttributes( { caption: val } ) }
					/>
					<ToggleControl
						label={ __( 'Show download button', 'brandy-blocks' ) }
						checked={ showDownload }
						onChange={ ( val ) => setAttributes( { showDownload: val } ) }
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ ! content ? (
					<Placeholder
						icon="qrcode"
						label={ __( 'QR Code', 'brandy-blocks' ) }
						instructions={ __(
							'Enter a URL or text in the sidebar to generate a QR code.',
							'brandy-blocks'
						) }
					>
						<TextControl
							label={ __( 'Content', 'brandy-blocks' ) }
							value={ content }
							type={ contentType === 'url' ? 'url' : 'text' }
							placeholder={
								contentType === 'url'
									? 'https://example.com'
									: __( 'Enter text…', 'brandy-blocks' )
							}
							onChange={ ( val ) => setAttributes( { content: val } ) }
						/>
					</Placeholder>
				) : (
					<div className="brandy-qr__preview" style={ { '--brandy-qr-fg': fgColor } }>
						<QRCodeSVG
							value={ content }
							size={ size }
							fgColor={ fgColor }
							bgColor={ bgColor }
							marginSize={ quietZone }
						/>
						{ caption && <p className="brandy-qr__caption">{ caption }</p> }
						{ showDownload && (
							<button
								type="button"
								className="brandy-qr__download"
								disabled
							>
								{ __( 'Download QR', 'brandy-blocks' ) }
							</button>
						) }
					</div>
				) }
			</div>
		</>
	);
}
